import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAutoReplyMessageAssets,
  getAutoReplySettings,
  TAutoReplyBusinessHour,
  TAutoReplyBusinessHourMode,
  TAutoReplyOutsideHourMode,
  upsertAutoReplySettings,
} from 'api';
import { Snackbar } from 'components/Snackbar';
import {
  autoReplyMessageStrings,
  autoReplySettingsQueryStrings,
} from 'constants/strings';
import { getQueryOrganizationId } from 'configs/queryClient';
import { queryKeys } from 'constants/queryKeys';
import { useCurrentUser } from 'hooks/useCurrentUser';
import {
  useOrganizationId,
  useUnsavedChangesGuard,
} from 'components/OrganizationScope/context';
import useUploadFile from 'hooks/useUploadFile';
import useToast from 'hooks/useToast';
import { TMessageSection } from 'types';
import { EOrganizationPlan } from 'types/enum';
import {
  areMessageAssetSectionsValid,
  areSurveySectionsValid,
  createDefaultAutoReplySections,
  hasFilledReplySection,
  isSectionFilled,
} from 'utils/messageSection';
import {
  convertDraftSectionsToMessageSections,
  formatSectionsForAPI,
} from '../SendMessageForAll/utils';
import MessagePreview from '../SendMessageForAll/MessagePreview';
import {
  AUTO_REPLY_TABS,
  AutoReplyTabKey,
  DEFAULT_BUSINESS_HOURS,
} from './constants';
import { KeywordReplyTab } from './KeywordReplyTab';
import { ResponseTimeTab } from './ResponseTimeTab';
import { UniformReplyTab } from './UniformReplyTab';
import { sendErrorMessages } from 'constants/errorStrings';
import { MessageAssetQueryError } from 'components/MessageAssetQueryError';
import { pageCardClasses } from 'components/AppLayout/styles';

export default function AutoReplyMessagePage() {
  const [activeTab, setActiveTab] = useState<AutoReplyTabKey>('uniform');
  const [uniformEnabled, setUniformEnabled] = useState(false);
  const [uniformSections, setUniformSections] = useState<TMessageSection[]>(
    () => createDefaultAutoReplySections(),
  );
  const [uniformChatLinkEnabled, setUniformChatLinkEnabled] = useState(false);
  const [uniformChatLinkText, setUniformChatLinkText] = useState('');
  const [uniformChatLinkLabel, setUniformChatLinkLabel] = useState('');
  const [keywordPreviewSections, setKeywordPreviewSections] = useState<
    TMessageSection[]
  >(() => createDefaultAutoReplySections());
  const [keywordPreviewChatLink, setKeywordPreviewChatLink] = useState<{
    text: string;
    label: string;
  } | null>(null);
  const [responseTimeEnabled, setResponseTimeEnabled] = useState(false);
  const [businessHours, setBusinessHours] = useState<TAutoReplyBusinessHour[]>(
    DEFAULT_BUSINESS_HOURS,
  );
  const [businessHourMode, setBusinessHourMode] =
    useState<TAutoReplyBusinessHourMode>('KEYWORD_ONLY');
  const [outsideHourMode, setOutsideHourMode] =
    useState<TAutoReplyOutsideHourMode>('NO_REPLY');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({
    open: false,
    message: '',
    variant: 'success',
  });
  const [initialized, setInitialized] = useState(false);
  // 2つのタブは別々に保存するため、未保存の判定も分ける
  const markUniformSaved = useUnsavedChangesGuard(
    JSON.stringify({
      uniformEnabled,
      uniformSections,
      uniformChatLinkEnabled,
      uniformChatLinkText,
      uniformChatLinkLabel,
    }),
  );
  const markResponseTimeSaved = useUnsavedChangesGuard(
    JSON.stringify({
      responseTimeEnabled,
      businessHours,
      businessHourMode,
      outsideHourMode,
    }),
  );

  const queryClient = useQueryClient();
  const { uploadToS3 } = useUploadFile();
  // アップロードの後に保存するため、送信先の組織を操作開始時に固定する
  const organizationId = useOrganizationId();
  const { toastError } = useToast();
  const { data: currentUser } = useCurrentUser();
  // LIGHTプランはチャット機能自体を使えないため、リンクを設定させない。
  // プランが分かるまでは操作させない（読み込み中に触れると設定できてしまう）。
  const chatLinkDisabled =
    !currentUser || currentUser.organizationPlan === EOrganizationPlan.LIGHT;

  const {
    data: settings,
    isError: settingsLoadError,
    isFetching: isFetchingSettings,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_SETTINGS],
    queryFn: getAutoReplySettings,
  });
  const {
    data: assets = [],
    isSuccess: assetsReady,
    isError: assetsLoadError,
    isFetching: isFetchingAssets,
    refetch: refetchAssets,
  } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
    queryFn: () => getAutoReplyMessageAssets(),
  });

  const areUniformAssetsValid = areMessageAssetSectionsValid(
    uniformSections,
    assets,
    assetsReady,
  );

  useEffect(() => {
    if (!settings || initialized) return;

    setInitialized(true);
    setUniformEnabled(settings.uniformReplyEnabled);
    setUniformSections(
      settings.uniformReplySections.length > 0
        ? convertDraftSectionsToMessageSections(settings.uniformReplySections)
        : createDefaultAutoReplySections(),
    );
    setUniformChatLinkEnabled(settings.uniformReplyChatLinkEnabled);
    setUniformChatLinkText(settings.uniformReplyChatLinkText ?? '');
    setUniformChatLinkLabel(settings.uniformReplyChatLinkLabel ?? '');
    setResponseTimeEnabled(settings.responseTimeEnabled);
    setBusinessHours(
      settings.businessHours.length > 0
        ? settings.businessHours
        : DEFAULT_BUSINESS_HOURS,
    );
    setBusinessHourMode(settings.businessHourMode);
    setOutsideHourMode(settings.outsideHourMode);
    markUniformSaved();
    markResponseTimeSaved();
  }, [settings, initialized, markUniformSaved, markResponseTimeSaved]);

  const settingsReady = settings !== undefined && !settingsLoadError;

  const saveSettingsMutation = useMutation({
    mutationFn: async (target: 'uniform' | 'time') => {
      if (!settings) {
        throw new Error(sendErrorMessages.settingsNotLoaded);
      }

      let nextUniformEnabled = settings.uniformReplyEnabled;
      let nextUniformSections = settings.uniformReplySections;
      let nextUniformChatLinkEnabled = settings.uniformReplyChatLinkEnabled;
      let nextUniformChatLinkText = settings.uniformReplyChatLinkText;
      let nextUniformChatLinkLabel = settings.uniformReplyChatLinkLabel;
      let nextResponseTimeEnabled = settings.responseTimeEnabled;
      let nextBusinessHours =
        settings.businessHours.length > 0
          ? settings.businessHours
          : DEFAULT_BUSINESS_HOURS;
      let nextBusinessHourMode = settings.businessHourMode;
      let nextOutsideHourMode = settings.outsideHourMode;

      if (target === 'uniform') {
        nextUniformEnabled = uniformEnabled;
        nextUniformChatLinkEnabled = uniformChatLinkEnabled;
        nextUniformChatLinkText = uniformChatLinkText.trim() || null;
        nextUniformChatLinkLabel = uniformChatLinkLabel.trim() || null;

        if (uniformEnabled) {
          if (!areUniformAssetsValid) {
            throw new Error(sendErrorMessages.invalidAssetSelection);
          }
          if (!areSurveySectionsValid(uniformSections)) {
            throw new Error(sendErrorMessages.surveySelectionRequired);
          }
          if (!hasFilledReplySection(uniformSections)) {
            throw new Error(sendErrorMessages.noContent);
          }
        }

        const sectionsToSave = uniformEnabled
          ? uniformSections
          : uniformSections.filter(isSectionFilled);
        nextUniformSections = await formatSectionsForAPI(sectionsToSave, {
          uploadToS3,
          onError: toastError,
        });
        if (
          uniformEnabled &&
          hasFilledReplySection(uniformSections) &&
          nextUniformSections.length === 0
        ) {
          throw new Error(sendErrorMessages.noContent);
        }
      } else {
        nextResponseTimeEnabled = responseTimeEnabled;
        nextBusinessHours = businessHours;
        nextBusinessHourMode = businessHourMode;
        nextOutsideHourMode = outsideHourMode;
      }

      const savedSettings = await upsertAutoReplySettings(
        {
          uniformReplyEnabled: nextUniformEnabled,
          uniformReplySections: nextUniformSections,
          uniformReplyChatLinkEnabled: nextUniformChatLinkEnabled,
          uniformReplyChatLinkText: nextUniformChatLinkText,
          uniformReplyChatLinkLabel: nextUniformChatLinkLabel,
          responseTimeEnabled: nextResponseTimeEnabled,
          businessHours: nextBusinessHours,
          businessHourMode: nextBusinessHourMode,
          outsideHourMode: nextOutsideHourMode,
        },
        organizationId,
      );
      return { savedSettings, submittedSections: uniformSections };
    },
    // 戻る/進むで組織が変わった後に解決しても、別組織のキャッシュへ書かない
    onMutate: () => ({ queryOrganizationId: getQueryOrganizationId() }),
    onSuccess: ({ savedSettings, submittedSections }, target, context) => {
      const isSameOrganization =
        context?.queryOrganizationId === getQueryOrganizationId();
      if (isSameOrganization) {
        queryClient.setQueryData(
          [queryKeys.AUTO_REPLY_SETTINGS],
          savedSettings,
        );
      }
      // 保存開始後に編集されていないsectionだけをS3 URL等へ再同期する。
      // 現在の配列を基準にすることで、追加・削除・並べ替えとOFF時の未入力枠を保持し、
      // 未編集の動画は次回保存で再アップロードしない。応答時間タブの保存では触らない。
      if (target === 'uniform') {
        const normalizedSections = savedSettings.uniformReplySections.length > 0
          ? convertDraftSectionsToMessageSections(savedSettings.uniformReplySections)
          : [];
        setUniformSections((currentSections) =>
          currentSections.map((section) => {
            if (!submittedSections.includes(section)) return section;
            return (
              normalizedSections.find((saved) => saved.id === section.id) ?? section
            );
          }),
        );
        markUniformSaved();
      } else {
        markResponseTimeSaved();
      }
      if (isSameOrganization) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.AUTO_REPLY_SETTINGS],
        });
      }
      showSnackbar(autoReplyMessageStrings.messages.saveSuccess, 'success');
    },
    onError: (error: Error) => {
      showSnackbar(
        error.message || autoReplyMessageStrings.messages.saveFailed,
        'error',
      );
    },
  });

  const activeDescription = useMemo(() => {
    if (activeTab === 'uniform')
      return autoReplyMessageStrings.uniformDescription;
    if (activeTab === 'keyword')
      return autoReplyMessageStrings.keywordDescription;
    return autoReplyMessageStrings.timeDescription;
  }, [activeTab]);

  const previewSections = useMemo(() => {
    if (activeTab === 'uniform') return uniformSections;
    if (activeTab === 'keyword') return keywordPreviewSections;
    return [];
  }, [activeTab, uniformSections, keywordPreviewSections]);

  // 実際の応答と同じく、本文の後ろに独立したCTAが届くことを見せる。
  // 未入力の欄は backend と同じく既定文言へ落とす。
  const previewChatLinkCta = useMemo(() => {
    if (chatLinkDisabled) return undefined;
    const source =
      activeTab === 'uniform'
        ? uniformChatLinkEnabled
          ? { text: uniformChatLinkText, label: uniformChatLinkLabel }
          : null
        : activeTab === 'keyword'
          ? keywordPreviewChatLink
          : null;
    if (!source) return undefined;

    return {
      text: source.text.trim() || autoReplyMessageStrings.chatLink.previewText,
      label:
        source.label.trim() || autoReplyMessageStrings.chatLink.previewButton,
    };
  }, [
    activeTab,
    chatLinkDisabled,
    uniformChatLinkEnabled,
    uniformChatLinkText,
    uniformChatLinkLabel,
    keywordPreviewChatLink,
  ]);

  const showSnackbar = (message: string, variant: 'success' | 'error') => {
    setSnackbar({ open: true, message, variant });
  };

  return (
    <>
      <section
        className={`w-full lg:h-[calc(100vh-12rem)] flex flex-col lg:overflow-hidden ${pageCardClasses.regular}`}
      >
        <header className="flex-shrink-0 mb-6">
          <h1 className="text-2xl font-display font-bold text-ink">
            {autoReplyMessageStrings.title}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {autoReplyMessageStrings.description}
          </p>
        </header>

        <div className="flex-shrink-0 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex gap-1 border-b border-border/70" role="tablist">
            {AUTO_REPLY_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-semibold transition border-b-2 -mb-px ${
                  activeTab === tab
                    ? 'border-accent text-accent'
                    : 'border-transparent text-muted hover:text-ink'
                }`}
              >
                {autoReplyMessageStrings.tabs[tab]}
              </button>
            ))}
          </div>
          <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
            {activeDescription}
          </span>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-10 flex-1 min-h-0">
          <div className="flex-1 min-h-0 lg:overflow-y-auto overflow-x-hidden lg:pr-2">
            {settingsLoadError ? (
              <div className="mb-4">
                <MessageAssetQueryError
                  message={autoReplySettingsQueryStrings.loadFailed}
                  retryLabel={autoReplySettingsQueryStrings.retry}
                  retryingLabel={autoReplySettingsQueryStrings.retrying}
                  isRetrying={isFetchingSettings}
                  onRetry={() => {
                    void refetchSettings();
                  }}
                />
              </div>
            ) : null}
            {assetsLoadError && activeTab !== 'time' ? (
              <div className="mb-4">
                <MessageAssetQueryError
                  isRetrying={isFetchingAssets}
                  onRetry={() => {
                    void refetchAssets();
                  }}
                />
              </div>
            ) : null}
            {activeTab === 'uniform' && (
              <UniformReplyTab
                enabled={uniformEnabled}
                sections={uniformSections}
                assets={assets}
                canSave={
                  (!uniformEnabled || areUniformAssetsValid) && settingsReady
                }
                isSaving={saveSettingsMutation.isPending}
                chatLinkEnabled={uniformChatLinkEnabled}
                chatLinkText={uniformChatLinkText}
                chatLinkLabel={uniformChatLinkLabel}
                chatLinkDisabled={chatLinkDisabled}
                onEnabledChange={setUniformEnabled}
                onSectionsChange={setUniformSections}
                onChatLinkEnabledChange={setUniformChatLinkEnabled}
                onChatLinkTextChange={setUniformChatLinkText}
                onChatLinkLabelChange={setUniformChatLinkLabel}
                onSave={() => saveSettingsMutation.mutate('uniform')}
              />
            )}

            {activeTab === 'keyword' && (
              <KeywordReplyTab
                assets={assets}
                assetsReady={assetsReady}
                chatLinkDisabled={chatLinkDisabled}
                onPreviewSectionsChange={setKeywordPreviewSections}
                onPreviewChatLinkChange={setKeywordPreviewChatLink}
                onSaveSuccess={(message) => showSnackbar(message, 'success')}
                onSaveError={(message) => showSnackbar(message, 'error')}
              />
            )}

            {activeTab === 'time' && (
              <ResponseTimeTab
                enabled={responseTimeEnabled}
                businessHours={businessHours}
                businessHourMode={businessHourMode}
                outsideHourMode={outsideHourMode}
                isSaving={saveSettingsMutation.isPending}
                canSave={settingsReady}
                onEnabledChange={setResponseTimeEnabled}
                onBusinessHoursChange={setBusinessHours}
                onBusinessHourModeChange={setBusinessHourMode}
                onOutsideHourModeChange={setOutsideHourMode}
                onSave={() => saveSettingsMutation.mutate('time')}
              />
            )}
          </div>

          {activeTab !== 'time' ? (
            <div className="flex lg:shrink-0 items-center justify-center w-full h-[460px] sm:h-[540px] lg:h-full lg:w-[400px] lg:min-h-[700px] xl:-mt-[150px]">
              <div
                className="relative w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[375px] mx-auto h-full lg:mx-0 lg:mr-2 mb-2 rounded-[3rem] border-8 border-white
             shadow-2xl flex flex-col overflow-hidden preview-phone-shadow z-10"
              >
                <MessagePreview
                  sections={previewSections}
                  assets={assets}
                  chatLinkCta={previewChatLinkCta}
                />
              </div>
            </div>
          ) : null}
        </div>
      </section>
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        variant={snackbar.variant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
